<template>
  <div>
    <div class="d-flex align-center mb-2" style="gap: 10px">
      <label for="button-show" v-if="label" class="mb-0">{{ label }}</label>
      <input
        id="button-show"
        type="checkbox"
        :checked="safeButton.show"
        @change="e => updateButton('show', !!e.target.checked)"
      />
    </div>
    <div v-if="safeButton.show" :class="['group-item', wrapperClass]">
      <v-row>
        <v-col cols="12" md="4">
          <div>
            <label for="button-text">Button Text</label>
            <v-text-field
              :value="safeButton.text"
              id="button-text"
              dense
              hide-details
              @input="value => updateButton('text', value)"
            />
          </div>
        </v-col>
        <v-col cols="12" md="4">
          <div>
            <label for="button-link">Button Link</label>
            <v-text-field
              :value="safeButton.link"
              id="button-link"
              dense
              hide-details
              @input="value => updateButton('link', value)"
            />
          </div>
        </v-col>
        <v-col cols="12" md="4">
          <div>
            <label for="button-align">Align</label>
            <v-select
              :value="safeButton.align"
              :items="[
                {
                  text: 'Center',
                  value: 'center',
                },
                {
                  text: 'Left',
                  value: 'flex-start',
                },
                {
                  text: 'Right',
                  value: 'flex-end',
                },
              ]"
              hide-details
              dense
              @change="value => updateButton('align', value || 'center')"
            />
          </div>
        </v-col>
        <v-col cols="12" md="4">
          <ColorPicker
            :value="safeButton.backgroundColor"
            name="btn-background"
            label="Background"
            default-color="#606F99"
            @input="value => updateButton('backgroundColor', value)"
          />
        </v-col>
        <v-col cols="12" md="4">
          <ColorPicker
            :value="safeButton.color"
            name="btn-text-color"
            label="Text Color"
            default-color="#FFFFFF"
            @input="value => updateButton('color', value)"
          />
        </v-col>
        <v-col cols="12" md="4">
          <div>
            <label for="button-radius">Radius</label>
            <v-text-field
              :value="safeButton.radius"
              id="button-radius"
              type="number"
              dense
              min="0"
              suffix="px"
              hide-details
              @input="value => updateButton('radius', normalizeRadius(value))"
            />
          </div>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="6">
          <div v-if="showIconPrefix" class="mt-4">
            <div class="d-flex align-center" style="gap: 10px">
              <label for="icon-prefix-show" class="font-weight-medium">Icon Prefix</label>
              <input
                id="icon-prefix-show"
                type="checkbox"
                :checked="safeButton.iconPrefix.show"
                @change="e => updateIcon('iconPrefix', 'show', !!e.target.checked)"
              />
            </div>
            <div>
              <UploadImage
                :image="{ imageSrc: safeButton.iconPrefix.src }"
                :height="`80`"
                :typeUpload="'image'"
                @callback="obj => updateIcon('iconPrefix', 'src', (obj && obj.imageSrc) || '')"
                @callbackDelete="() => updateIcon('iconPrefix', 'src', '')"
              />
            </div>
          </div>
        </v-col>
        <v-col cols="12" md="6">
          <div v-if="showIconSuffix" class="mt-4">
            <div class="d-flex align-center" style="gap: 10px">
              <label for="icon-suffix-show" class="font-weight-medium">Icon Suffix</label>
              <input
                id="icon-suffix-show"
                type="checkbox"
                :checked="safeButton.iconSuffix.show"
                @change="e => updateIcon('iconSuffix', 'show', !!e.target.checked)"
              />
            </div>
            <div>
              <UploadImage
                :image="{ imageSrc: safeButton.iconSuffix.src }"
                :height="`80`"
                :typeUpload="'image'"
                @callback="obj => updateIcon('iconSuffix', 'src', (obj && obj.imageSrc) || '')"
                @callbackDelete="() => updateIcon('iconSuffix', 'src', '')"
              />
            </div>
          </div>
        </v-col>
      </v-row>

      <div v-if="showSubBtn" class="mt-4">
        <div class="d-flex align-center" style="gap: 10px">
          <label for="sub-button-show" class="font-weight-medium">Sub Button</label>
          <input
            id="sub-button-show"
            type="checkbox"
            :checked="safeButton.subBtn.show"
            @change="e => updateSubBtn('show', !!e.target.checked)"
          />
        </div>
        <v-text-field
          :value="safeButton.subBtn.text"
          id="sub-button-text"
          dense
          hide-details
          @input="value => updateSubBtn('text', value)"
        />
      </div>
    </div>

    <div v-if="showPolicy" class="mt-4">
      <div class="d-flex align-center mb-2" style="gap: 10px">
        <label for="policy-show" class="font-weight-medium">Policy</label>
        <input
          id="policy-show"
          type="checkbox"
          :checked="safeButton.policy.show"
          @change="e => updatePolicy('show', !!e.target.checked)"
        />
      </div>
      <div v-if="safeButton.policy.show" class="group-item">
        <div class="d-flex align-end justify-space-between" style="gap: 10px">
          <div>
            <div>Align Content</div>
            <v-select
              :value="safeButton.policy.alignContent"
              :items="['center', 'left', 'right']"
              hide-details
              dense
              @change="value => updatePolicy('alignContent', value)"
            />
          </div>
          <div>
            <v-btn small color="primary" @click="addPolicy">
              <v-icon size="16">mdi-plus</v-icon>
              <span>Add Policy</span>
            </v-btn>
          </div>
        </div>
        <div class="mt-4">
          <v-expansion-panels small accordion>
            <v-expansion-panel v-for="(item, i) in safeButton.policy.items" :key="`policy-${i}`">
              <v-expansion-panel-header class="py-0">
                <div class="d-flex justify-space-between align-center">
                  <span>Policy {{ i + 1 }}</span>
                  <v-btn small icon @click.stop="removePolicy(i)">
                    <v-icon size="20" color="#ff6c6c">mdi-delete</v-icon>
                  </v-btn>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div>
                  <UploadImage
                    :image="{ imageSrc: item.icon }"
                    :height="`80`"
                    :typeUpload="'image'"
                    @callback="obj => handleUploadPolicyIcon(obj, i)"
                    @callbackDelete="() => handleRemovePolicyIcon(i)"
                  />
                </div>
                <div>
                  <label for="policy-text">Policy Text</label>
                  <v-text-field
                    :value="item.text"
                    @input="value => updatePolicyItem('text', value, i)"
                    id="policy-text"
                    dense
                    hide-details
                  />
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import UploadImage from '@/views/website/themes/homepage/common/form/upload-file/upload-image.vue';
import ColorPicker from '../inputs/color-picker.vue';

const defaultButton = {
  align: 'flex-start',
  backgroundColor: '#606F99',
  color: '#FFFFFF',
  radius: 4,
  iconPrefix: {
    show: false,
    src: '',
  },
  iconSuffix: {
    show: true,
    src: 'https://cdn.skyfastimg.com/6a0ee559dc2c2e000934dd37/2026/06/03/image-6a1f8e0dd4865b45728c40d9.png',
  },
  text: 'Shop Now',
  link: '#',
  show: true,
  subBtn: {
    show: false,
    text: '30-Day Money-Back Guararantee',
  },
  policy: {
    show: false,
    colPerView: 2,
    alignContent: 'center',
    items: [],
  },
};

export default {
  name: 'ButtonPrimary',
  components: {
    UploadImage,
    ColorPicker,
  },
  props: {
    button: {
      type: Object,
      required: true,
      default: () => ({ ...defaultButton }),
    },
    label: {
      type: String,
      default: 'Button',
    },
    wrapperClass: {
      type: String,
      default: '',
    },
    showIconPrefix: {
      type: Boolean,
      default: true,
    },
    showIconSuffix: {
      type: Boolean,
      default: true,
    },
    showSubBtn: {
      type: Boolean,
      default: true,
    },
    showPolicy: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    safeButton() {
      const src = this.button || {};
      return {
        ...defaultButton,
        ...src,
        iconPrefix: { ...defaultButton.iconPrefix, ...(src.iconPrefix || {}) },
        iconSuffix: { ...defaultButton.iconSuffix, ...(src.iconSuffix || {}) },
        subBtn: { ...defaultButton.subBtn, ...(src.subBtn || {}) },
        policy: { ...defaultButton.policy, ...(src.policy || {}) },
      };
    },
  },
  methods: {
    addPolicy() {
      if (!this.button.policy) this.$set(this.button, 'policy', { ...defaultButton.policy });
      this.button.policy.items.push({ icon: '', text: '' });
      this.emitChange();
    },
    removePolicy(index) {
      this.button.policy.items.splice(index, 1);
      this.emitChange();
    },
    handleUploadPolicyIcon(obj, index) {
      this.button.policy.items[index].icon = obj.imageSrc;
      this.emitChange();
    },
    handleRemovePolicyIcon(index) {
      this.button.policy.items[index].icon = '';
      this.emitChange();
    },
    updatePolicyItem(key, value, index) {
      this.button.policy.items[index][key] = value;
      this.emitChange();
    },
    emitChange() {
      this.$emit('update:button', this.button);
      this.$emit('change', this.button);
    },
    updateButton(key, value) {
      console.log(key, value);
      this.$set(this.button, key, value);
      this.emitChange();
    },
    updatePolicy(key, value) {
      if (!this.button.policy) this.$set(this.button, 'policy', { ...defaultButton.policy });
      this.$set(this.button.policy, key, value);
      this.emitChange();
    },
    updateIcon(slot, key, value) {
      if (!this.button[slot]) this.$set(this.button, slot, { ...defaultButton[slot] });
      this.$set(this.button[slot], key, value);
      this.emitChange();
    },
    updateSubBtn(key, value) {
      if (!this.button.subBtn) this.$set(this.button, 'subBtn', { ...defaultButton.subBtn });
      this.$set(this.button.subBtn, key, value);
      this.emitChange();
    },
    normalizeRadius(value) {
      const num = Number(value);
      if (Number.isNaN(num) || num < 0) return 0;
      return num;
    },
  },
};
</script>

<style scoped lang="scss">
.group-item {
  padding: 15px 10px;
  border-radius: 10px;
  border: 1px solid #e0e0e0;
}
.label-input {
  font-size: 18px !important;
  font-weight: 500;
  color: #000;
  margin-bottom: 10px;
  display: block;
}
</style>
